//
//  Project.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: October 22, 2026
//  Author: Bob Keathley
//
//  One project (§6s): agents (refine, Notebook, top-level), repos, rules,
//  skills, settings summaries and snapshot history, plus the per-project
//  release (§6t) with a preview of exactly what ships.
//
import { Fragment, useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import { api, getAssumed, subscribeAssumed } from '@shared/api/client'
import { isOk } from '@shared/api/envelope'
import {
  BIRTH_ALL_NOTE, forbidSource, notebookSourceLabel, type BirthAllResult, type NotebookRules, type ProjectSettings, agentSummary, agentText, groupByScope, scopeLabel, canEditProjects, canPublishProject, lineDiff, projectErrorText,
  type AgentVersion, type ProjectAgent, type ProjectDetail, type ProjectPage, type ProjectReleaseMeta, type SnapshotMeta,
  answersPreviewPath, notebookVersionsPath, rebirthPath, versionSourceLabel,
  type AnswersPreview, type NotebookVersions, type RebirthResult,
} from '@shared/api/projects'
import type { CanonSectors } from '@shared/api/types'
import { CanonInterview } from '../components/CanonInterview'
import { INTAKE_QUESTIONS } from '@shared/api/mandate'
import { canEditHierarchy, effectivePath, levelErrorText, levelLabel, rebirthAffectedPath, type Effective, type LevelTree, type Rebirth } from '@shared/api/levels'
import { templateLabel, type MandateTemplate } from '@shared/api/types'
import type { Session, TenantRole } from '@shared/auth'
import { href } from '@shared/router'
import { IntakeRow, toIntakeAnswers, toRules, useIntakeAnswers } from './Birth'
import { when } from './Projects'

const TABS = ['Agents', 'People', 'Repos', 'Rules', 'Skills', 'Settings', 'History'] as const
type Tab = (typeof TABS)[number]

/** Writes are team-admin+; an assumed read session can see but not change anything. */
export function projectWriteState(role: string | undefined, assumed: { mode: 'read' | 'write' } | null): { visible: boolean; reason: string | null } {
  if (!canEditProjects(role)) return { visible: false, reason: null }
  return { visible: true, reason: assumed?.mode === 'read' ? 'This is an assumed read-only session.' : null }
}
/** Publish is security-admin+. */
export function projectPublishState(role: string | undefined, assumed: { mode: 'read' | 'write' } | null): { visible: boolean; reason: string | null } {
  if (!canPublishProject(role)) return { visible: false, reason: null }
  return { visible: true, reason: assumed?.mode === 'read' ? 'This is an assumed read-only session; it cannot publish.' : null }
}

const short = (s: string) => s.slice(0, 12)

/** "N machine paths removed" where the server stripped home paths from an item's text (§6s). */
function PathsRemoved({ n }: { n?: number }) {
  return n && n > 0 ? <div className="hint">{n} machine path{n === 1 ? '' : 's'} removed</div> : null
}

/** Table rows grouped under a scope heading (§6s scopes). */
function ScopedRows<T extends { scope: ProjectAgent['scope']; depth: number | null }>({ items, cols, row }: { items: T[]; cols: number; row: (t: T) => React.ReactNode }) {
  return (
    <>
      {groupByScope(items).map((g) => (
        <Fragment key={g.key}>
          <tr><th colSpan={cols} scope="colgroup" style={{ textAlign: 'left' }}>{g.label}</th></tr>
          {g.items.map(row)}
        </Fragment>
      ))}
    </>
  )
}

/** A settings permission list; only deny is enforced by a Notebook (Claude's allow/ask mean no-prompt/prompt, not "only these"). */
function PatternList({ label, note, items }: { label: string; note: string; items?: string[] }) {
  if (!items || items.length === 0) return null
  return (
    <details>
      <summary className="hint">{label} patterns ({items.length}) · <b>{note}</b></summary>
      <ul style={{ margin: 0 }}>{items.map((x) => <li key={x} className="mono">{x}</li>)}</ul>
    </details>
  )
}

function Diff({ before, after }: { before: string; after: string }) {
  const lines = lineDiff(before, after)
  if (!lines.some((l) => l.kind !== 'same')) return <p className="hint">No difference from the scanned version.</p>
  return (
    <div className="diff" role="group" aria-label="Diff against the scanned version">
      {lines.map((l, i) => <div key={i} className={l.kind === 'same' ? undefined : l.kind}>{l.kind === 'add' ? '+ ' : l.kind === 'del' ? '- ' : '  '}{l.text}</div>)}
    </div>
  )
}

function AgentEditor({ projectId, agent, token, onSaved, onClose, onError }: { projectId: string; agent: ProjectAgent; token: string; onSaved: () => void; onClose: () => void; onError: (m: string) => void }) {
  const [fm, setFm] = useState(JSON.stringify(agent.current.frontmatter, null, 2))
  const [body, setBody] = useState(agent.current.body)
  const [scanned, setScanned] = useState<AgentVersion | null | 'none'>(null)
  const [busy, setBusy] = useState(false)
  const [fmErr, setFmErr] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    api<{ items: AgentVersion[] }>(`/projects/${projectId}/agents/${encodeURIComponent(agent.name)}/versions`, {}, token).then((r) => {
      if (!alive) return
      if (!isOk(r)) { onError(projectErrorText(r.error)); setScanned('none'); return }
      setScanned(r.data.items.find((v) => v.source === 'scan') ?? 'none')   // newest first
    })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, agent.name, token])

  let parsed: Record<string, unknown> | null = null
  try { const v = JSON.parse(fm); parsed = v && typeof v === 'object' && !Array.isArray(v) ? v : null } catch { parsed = null }

  async function save() {
    if (!parsed) { setFmErr('Frontmatter must be a JSON object.'); return }
    setFmErr(null); setBusy(true)
    const r = await api<AgentVersion>(`/projects/${projectId}/agents/${encodeURIComponent(agent.name)}/versions`, { method: 'POST', body: JSON.stringify({ frontmatter: parsed, body }) }, token)
    setBusy(false)
    if (!isOk(r)) { onError(projectErrorText(r.error)); return }
    onSaved()
  }

  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>Edit <span className="mono">{agent.name}</span></h3>
      <p className="hint">Saving makes a new tenant version. It reaches Macs only in the next project release.</p>
      <label htmlFor={`fm-${agent.name}`}>Frontmatter (JSON)</label>
      <textarea id={`fm-${agent.name}`} value={fm} onChange={(e) => setFm(e.target.value)} spellCheck={false} />
      {fmErr && <p className="err" role="alert">{fmErr}</p>}
      <label htmlFor={`body-${agent.name}`}>Body</label>
      <textarea id={`body-${agent.name}`} value={body} onChange={(e) => setBody(e.target.value)} style={{ minHeight: 220 }} />
      <h4>Changes from the scanned version</h4>
      {scanned === null && <p className="hint">Loading the scanned version…</p>}
      {scanned === 'none' && <p className="hint">No scanned version to compare against.</p>}
      {scanned && scanned !== 'none' && parsed && <Diff before={agentText(scanned.frontmatter, scanned.body)} after={agentText(parsed, body)} />}
      <p>
        <button className="btn" disabled={busy || !parsed} onClick={save}>{busy ? 'Saving…' : 'Save new version'}</button>{' '}
        <button className="btn ghost" onClick={onClose}>Cancel</button>
      </p>
    </div>
  )
}

const NO_PREVIEW_KEY = ''
const show = (v: unknown) => (typeof v === 'string' ? v : JSON.stringify(v))

/** The `answers` body of the answers routes: only what the person answered. Nothing is filled in, and the agent name is the server's. */
function answeredOnly(answers: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const q of INTAKE_QUESTIONS) {
    if (q.id === 'agent' || !(q.id in answers)) continue
    out[q.id] = q.id === 'never' || q.id === 'escalate' ? toRules(answers[q.id]) : answers[q.id]
  }
  return out
}

/** Every version of an agent's Notebook: who signed it, when, from what, and its hash. */
function NotebookVersionsList({ projectId, agent, token, refreshKey }: { projectId: string; agent: string; token: string; refreshKey: number }) {
  const [data, setData] = useState<NotebookVersions | 'none' | 'error' | null>(null)
  useEffect(() => {
    let alive = true
    setData(null)
    api<NotebookVersions>(notebookVersionsPath(projectId, agent), {}, token).then((r) => {
      if (!alive) return
      if (isOk(r)) setData(r.data); else setData(r.error.code === 'not_found' ? 'none' : 'error')
    })
    return () => { alive = false }
  }, [projectId, agent, token, refreshKey])
  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>Versions of <span className="mono">{agent}</span></h3>
      {data === null && <p className="hint">Loading…</p>}
      {data === 'none' && <p className="hint">This agent has no Notebook yet, so there are no versions.</p>}
      {data === 'error' && <p className="err" role="alert">The versions could not be loaded.</p>}
      {data && typeof data === 'object' && (
        <table>
          <thead><tr><th>Version</th><th>Signed by</th><th>When</th><th>Made from</th><th>Hash</th></tr></thead>
          <tbody>
            {data.items.map((v) => (
              <tr key={v.birth_cert_id}>
                <td className="mono">{v.notebook_version}{v.current && <> <span className="pill ok">current</span></>}</td>
                <td>{v.signed_by.name ?? <span className="mono">{short(v.signed_by.user_id)}</span>}</td>
                <td className="hint">{when(v.signed_at)}</td>
                <td>{versionSourceLabel(v.source)}</td>
                <td className="mono hint" title={v.compiled_hash}>{short(v.compiled_hash)}…</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/** One agent's questions: a person answers, previews the rule-by-rule change, and signs version n+1. */
function AnswersPanel({ projectId, agent, p, token, role, onSigned, onClose }: { projectId: string; agent: ProjectAgent; p: ProjectDetail; token: string; role: TenantRole; onSigned: (r: RebirthResult) => void; onClose: () => void }) {
  const { answers, checks, answerQuestion } = useIntakeAnswers(role, token)
  const questions = INTAKE_QUESTIONS.filter((q) => q.id !== 'agent')
  const [sectors, setSectors] = useState<CanonSectors | 'unavailable' | null>(null)
  const [sectorId, setSectorId] = useState('')
  const [canonAnswers, setCanonAnswers] = useState<string[]>([])
  const [accountability, setAccountability] = useState('')
  const [preview, setPreview] = useState<AnswersPreview | null>(null)
  const [previewKey, setPreviewKey] = useState(NO_PREVIEW_KEY)
  const [busy, setBusy] = useState<'preview' | 'sign' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [signed, setSigned] = useState<RebirthResult | null>(null)

  useEffect(() => {
    let alive = true
    api<CanonSectors>('/canon/sectors', {}, token).then((r) => {
      if (!alive) return
      if (isOk(r)) { setSectors(r.data); setCanonAnswers(new Array(r.data.axes.length).fill('')) } else setSectors('unavailable')
    })
    return () => { alive = false }
  }, [token])
  const sector = sectors !== 'unavailable' && sectors && sectorId ? sectors.sectors.find((s) => s.sector === sectorId) ?? null : null

  const canonReady = !!sectorId && canonAnswers.length > 0 && canonAnswers.every(Boolean) && accountability.trim().length > 0
  const canonStarted = !!sectorId || canonAnswers.some(Boolean) || accountability.trim().length > 0
  const body = { answers: answeredOnly(answers), ...(canonReady ? { canon: { sector: sectorId, answers: canonAnswers, accountability: accountability.trim() } } : {}) }
  const bodyKey = JSON.stringify(body)
  const fresh = !!preview && previewKey === bodyKey
  const notebookRules = agent.notebook && agent.notebook.tool_allowlist !== undefined ? agent.notebook : null

  async function runPreview() {
    setBusy('preview'); setError(null); setSigned(null)
    const r = await api<AnswersPreview>(answersPreviewPath(projectId, agent.name), { method: 'POST', body: bodyKey }, token)
    setBusy(null)
    if (!isOk(r)) { setPreview(null); setError(r.error.code === 'authority_ceiling' ? `Refused: ${r.error.message}` : projectErrorText(r.error)); return }
    setPreview(r.data); setPreviewKey(bodyKey)
  }
  async function sign() {
    setBusy('sign'); setError(null)
    const r = await api<RebirthResult>(rebirthPath(projectId, agent.name), { method: 'POST', body: bodyKey }, token)
    setBusy(null)
    if (!isOk(r)) {
      const d = (r.error.details ?? {}) as { code?: string }
      setError(d.code === 'no_change' ? 'Nothing changed: these answers give the same Notebook as the current version, so nothing was signed.' : r.error.code === 'authority_ceiling' ? `Refused: ${r.error.message}` : projectErrorText(r.error))
      return
    }
    setSigned(r.data); setPreview(null); onSigned(r.data)
  }
  const goPublish = () => document.getElementById('project-releases')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  const nextVersion = preview?.next_version

  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>Answer questions for <span className="mono">{agent.name}</span></h3>
      <p className="hint">You answer; nothing is filled in from the agent's file. Your answers can only tighten the rules the file already sets. Signing makes a new version of this agent's Notebook.</p>
      <div className="answers-grid">
        <div className="col">
          <h4 style={{ marginTop: 0 }}>The agent's file <span className="hint">(read only)</span></h4>
          <p className="hint mono">{agent.current.path_rel}</p>
          <pre className="body">{JSON.stringify(agent.current.frontmatter, null, 2)}</pre>
          <pre className="body">{agent.current.body}</pre>
          <h4>Rules imported from the file</h4>
          {notebookRules
            ? <NotebookRulesView rules={notebookRules} agent={agent} settings={p.settings} />
            : <p className="hint">The imported rules are listed in the preview.</p>}
        </div>
        <div className="col">
          <h4 style={{ marginTop: 0 }}>Your answers</h4>
          <p className="hint">Leave a question empty to leave that rule to the file. Each answer is checked against your authority as you give it.</p>
          {questions.map((q) => <IntakeRow key={q.id} q={q} value={answers[q.id]} onAnswer={(v) => answerQuestion(q, v)} check={checks[q.id]} role={role} />)}
          <h4>Optional — canon identity (not required yet)</h4>
          <p className="hint">Nine questions, then a line on who is accountable for this agent. It is included only once all of it is filled in.</p>
          <CanonInterview
            sectors={sectors}
            sector={sector}
            sectorId={sectorId}
            onSector={(sid) => { setSectorId(sid); setCanonAnswers(new Array(sectors !== 'unavailable' && sectors ? sectors.axes.length : 0).fill('')) }}
            answers={canonAnswers}
            onAnswer={(i, v) => { const next = [...canonAnswers]; next[i] = v; setCanonAnswers(next) }}
            canon={null}
            role={role}
            sectorOptional
          />
          <label htmlFor={`acc-${agent.name}`}>Who is accountable for this agent, and for what?</label>
          <textarea id={`acc-${agent.name}`} value={accountability} onChange={(e) => setAccountability(e.target.value)} style={{ minHeight: 70 }} />
        </div>
      </div>
      {error && <p className="err" role="alert">{error}</p>}
      <p>
        <button className="btn" disabled={busy !== null} onClick={runPreview}>{busy === 'preview' ? 'Previewing…' : 'Preview'}</button>{' '}
        <button className="btn" disabled={busy !== null || !fresh || preview?.no_change !== false} onClick={sign}>{busy === 'sign' ? 'Signing…' : `Birth and sign version ${nextVersion ?? 'n+1'}`}</button>{' '}
        <button className="btn ghost" disabled={busy !== null} onClick={onClose}>Close</button>
        {canonStarted && !canonReady && <span className="hint"> The canon identity is not complete, so it won't be included.</span>}
        {preview && !fresh && <span className="hint"> Preview after any change to sign.</span>}
      </p>
      {preview && (
        <div role="status">
          <h4>Change against {preview.current_version === null ? 'no earlier version' : `version ${preview.current_version}`} → version {preview.next_version}</h4>
          {preview.no_change && <p className="hint">These answers give exactly the Notebook that is already current, so there is nothing to sign.</p>}
          {preview.diff.added.length > 0 && <><h4>Added</h4><ul style={{ margin: 0 }}>{preview.diff.added.map((d, i) => <li key={`a${i}`}><span className="mono">{d.key}</span> {show(d.value)} <span className="hint">· {d.source}</span></li>)}</ul></>}
          {preview.diff.tightened.length > 0 && <><h4>Tightened</h4><ul style={{ margin: 0 }}>{preview.diff.tightened.map((d, i) => <li key={`t${i}`}><span className="mono">{d.key}</span> {show(d.from)} → {show(d.to)} <span className="hint">· {d.source}</span></li>)}</ul></>}
          {(preview.diff.removed ?? []).length > 0 && <><h4>Removed from the current version</h4><p className="hint">A re-birth uses the imported rules and only the answers you gave this time, so an earlier answer you did not repeat drops out.</p><ul style={{ margin: 0 }}>{(preview.diff.removed ?? []).map((d, i) => <li key={`r${i}`}><span className="mono">{d.key}</span> {show(d.value)}</li>)}</ul></>}
          {(preview.diff.loosened ?? []).length > 0 && <><h4>Looser than the current version</h4><ul style={{ margin: 0 }}>{(preview.diff.loosened ?? []).map((d, i) => <li key={`l${i}`}><span className="mono">{d.key}</span> {show(d.from)} → {show(d.to)} <span className="hint">· {d.source}</span></li>)}</ul></>}
          {preview.diff.unchanged.length > 0 && <details><summary className="hint">Unchanged ({preview.diff.unchanged.length})</summary><ul style={{ margin: 0 }}>{preview.diff.unchanged.map((d, i) => <li key={`u${i}`}><span className="mono">{d.key}</span> {show(d.value)}</li>)}</ul></details>}
          <h4>Every rule, and where it comes from</h4>
          <table className="lineage">
            <thead><tr><th>Rule</th><th>Value</th><th>Comes from</th></tr></thead>
            <tbody>
              {preview.lineage.length === 0 && <tr><td colSpan={3} className="hint">No rules.</td></tr>}
              {preview.lineage.map((l, i) => (
                <tr key={i} className={l.origin}>
                  <td className="mono">{l.key}</td>
                  <td className="mono">{show(l.value)}</td>
                  <td><span className={`pill ${l.origin === 'answer' ? 'owner' : l.origin === 'default' ? 'off' : ''}`}>{l.origin === 'answer' ? 'from your answer' : l.origin === 'default' ? "default (your role's limit)" : 'from the file'}</span> <span className="hint mono">{l.source}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <h4>Canon placement</h4>
          {preview.canon === null ? <p className="hint">No canon identity in this version.</p> : (
            <>
              <p className="hint">
                {preview.canon.reused ? 'The canon is unchanged, so the current canon record is reused.' : 'This is a new canon placement; it is stored when you sign.'}
                {preview.canon.canon_birth_id && <> Canon record <span className="mono">{short(preview.canon.canon_birth_id)}</span>.</>}
              </p>
              <p>Identity <span className="mono">{preview.canon.placement.rn_id}</span> · {preview.canon.placement.status === 'QUARANTINE-REVIEW' ? <span className="pill off">QUARANTINE-REVIEW</span> : <span className="pill ok">{preview.canon.placement.status}</span>}</p>
              <p className="hint">Coordinates: <span className="mono">{preview.canon.placement.coords.map((c) => (typeof c === 'number' ? c.toFixed(3) : String(c))).join(', ')}</span></p>
            </>
          )}
          <h4>Not answered ({preview.unanswered.length})</h4>
          {preview.unanswered.length === 0
            ? <p className="hint">Every question has an answer.</p>
            : <p className="hint">The file alone governs these: <span className="mono">{preview.unanswered.join(', ')}</span></p>}
          {preview.unanswered.includes('budget') && <p className="hint">Budget is not answered, so your role's limit applies as the default.</p>}
        </div>
      )}
      {signed && (
        <p role="status">
          Signed version {signed.notebook_version} of <span className="mono">{agent.name}</span> (hash <span className="mono" title={signed.compiled_hash}>{short(signed.compiled_hash)}</span>).{' '}
          <button className="linklike" onClick={goPublish}>Publish the project release to send version {signed.notebook_version} to the Mac</button>
        </p>
      )}
    </div>
  )
}

function NotebookPicker({ projectId, agent, token, role, onDone, onClose, onError }: { projectId: string; agent: ProjectAgent; token: string; role: TenantRole; onDone: () => void; onClose: () => void; onError: (m: string) => void }) {
  const [mode, setMode] = useState<'intake' | 'template'>('intake')
  const [templates, setTemplates] = useState<MandateTemplate[] | 'unavailable' | null>(null)
  const [templateId, setTemplateId] = useState('')
  const [busy, setBusy] = useState(false)
  const { answers, checks, answerQuestion } = useIntakeAnswers(role, token)
  const questions = INTAKE_QUESTIONS.filter((q) => q.id !== 'agent')   // the agent name is this agent's, fixed
  const answered = questions.every((q) => q.id in answers)
  useEffect(() => {
    if (mode !== 'template' || templates !== null) return
    let alive = true
    api<ProjectPage<MandateTemplate>>('/mandates/templates', {}, token).then((r) => {
      if (!alive) return
      setTemplates(isOk(r) ? r.data.items.filter((t) => (t.content_type ?? 'template') === 'template' && !t.provenance.retired_at) : 'unavailable')
    })
    return () => { alive = false }
  }, [mode, templates, token])
  async function birth() {
    setBusy(true)
    const body = mode === 'intake' ? { answers: toIntakeAnswers({ ...answers, agent: agent.name }) } : { template: templateId }
    const r = await api<unknown>(`/projects/${projectId}/agents/${encodeURIComponent(agent.name)}/notebook`, { method: 'PUT', body: JSON.stringify(body) }, token)
    setBusy(false)
    if (!isOk(r)) { onError(r.error.code === 'template_not_assigned' ? 'The platform has not assigned this template to your organisation.' : projectErrorText(r.error)); return }
    onDone()
  }
  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>Notebook for <span className="mono">{agent.name}</span></h3>
      <p className="hint">Signs this agent's Notebook now. It ships in the next project release.</p>
      <div className="row" role="radiogroup" aria-label="How to birth this Notebook">
        <button className={`btn fixed ${mode === 'intake' ? '' : 'ghost'}`} role="radio" aria-checked={mode === 'intake'} onClick={() => setMode('intake')}>Answer the intake questions</button>
        <button className={`btn fixed ${mode === 'template' ? '' : 'ghost'}`} role="radio" aria-checked={mode === 'template'} onClick={() => setMode('template')}>From an assigned template</button>
      </div>
      {mode === 'intake' && (
        <>
          <p className="hint">The agent name is fixed to <span className="mono">{agent.name}</span>. You answer every other question yourself; nothing is filled in from the agent's file. Each answer is checked against your authority as you give it.</p>
          {questions.map((q) => <IntakeRow key={q.id} q={q} value={answers[q.id]} onAnswer={(v) => answerQuestion(q, v)} check={checks[q.id]} role={role} />)}
          {!answered && <p className="hint">Answer every question (or accept a refusal) to continue.</p>}
        </>
      )}
      {mode === 'template' && (
        <>
          {templates === null && <p className="hint">Loading templates…</p>}
          {templates === 'unavailable' && <p className="hint">The template catalogue isn't reachable right now.</p>}
          {Array.isArray(templates) && templates.length === 0 && <p className="hint">No templates assigned. Ask the platform to assign one, or answer the intake questions.</p>}
          {Array.isArray(templates) && templates.length > 0 && (
            <>
              <label htmlFor={`tpl-${agent.name}`}>Template</label>
              <select id={`tpl-${agent.name}`} value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                <option value="">Choose a template…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{templateLabel(t)} v{t.version} ({t.source})</option>)}
              </select>
            </>
          )}
        </>
      )}
      <p>
        <button className="btn" disabled={busy || (mode === 'intake' ? !answered : !templateId)} onClick={birth}>{busy ? 'Signing…' : 'Birth Notebook'}</button>{' '}
        <button className="btn ghost" onClick={onClose}>Cancel</button>
      </p>
    </div>
  )
}

/** What §6t will compose from current state: every agent's current version, every Notebook born for the project, the top-level agent. */
function PublishPreview({ p }: { p: ProjectDetail }) {
  const notebooks = p.agents.filter((a) => a.notebook)
  return (
    <div className="panel" aria-label="Release preview">
      <h3 style={{ marginTop: 0 }}>This release will ship</h3>
      <p className="hint">Claude agent files: not shipped — the files on the Mac stay as they are.</p>
      <h4>Notebooks ({notebooks.length})</h4>
      {notebooks.length === 0 ? <p className="hint">None. No agent has a Notebook yet.</p> : (
        <ul style={{ margin: 0 }}>
          {notebooks.map((a) => (
            <li key={a.name}>
              <span className="mono">{a.name}</span> — {notebookSourceLabel(a.notebook!)}
              {' · '}born hash <span className="mono" title={a.notebook!.compiled_hash}>{short(a.notebook!.compiled_hash)}</span>
              {(a.notebook!.tool_allowlist !== undefined || a.notebook!.forbid_bash || a.notebook!.forbid_tool) && <NotebookRulesView rules={a.notebook!} agent={a} settings={p.settings} />}
            </li>
          ))}
        </ul>
      )}
      <h4>Top-level agent</h4>
      <p>{p.top_level_agent ? <span className="mono">{p.top_level_agent}</span> : <span className="hint">None set.</span>}</p>
      <p className="hint">Releases are append-only: signed with your organisation's key, and cannot be edited or deleted.</p>
    </div>
  )
}

function ReleasesPanel({ p, session, onChanged }: { p: ProjectDetail; session: Session; onChanged: () => void }) {
  const assumed = useSyncExternalStore(subscribeAssumed, getAssumed, getAssumed)
  const gate = projectPublishState(session.user.role, assumed)
  const [releases, setReleases] = useState<ProjectReleaseMeta[] | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const load = useCallback(async () => {
    const r = await api<ProjectPage<ProjectReleaseMeta>>(`/projects/${p.id}/releases`, {}, session.token)
    if (isOk(r)) setReleases(r.data.items); else { setReleases([]); setError(r.error.message) }
  }, [p.id, session.token])
  useEffect(() => { load() }, [load])
  async function publish() {
    setBusy(true); setError(null); setNotice(null)
    const r = await api<unknown>(`/projects/${p.id}/releases`, { method: 'POST', body: JSON.stringify({}) }, session.token)
    setBusy(false)
    if (!isOk(r)) { setError(projectErrorText(r.error)); return }
    setPreviewing(false); setNotice('Release published.'); load(); onChanged()
  }
  return (
    <>
      <h3 id="project-releases">Releases</h3>
      {error && <p className="err" role="alert">{error}</p>}
      {notice && <p className="hint" role="status">{notice}</p>}
      {gate.visible && !previewing && (
        <p>
          <button className="btn" disabled={!!gate.reason} title={gate.reason ?? undefined} onClick={() => setPreviewing(true)}>Publish release</button>
          {gate.reason && <span className="hint"> {gate.reason}</span>}
        </p>
      )}
      {gate.visible && previewing && (
        <>
          <PublishPreview p={p} />
          <p>
            <button className="btn" disabled={busy || !p.agents.some((a) => a.notebook)} onClick={publish}>{busy ? 'Signing…' : 'Sign and publish'}</button>{' '}
            <button className="btn ghost" disabled={busy} onClick={() => setPreviewing(false)}>Cancel</button>
            {!p.agents.some((a) => a.notebook) && <span className="hint"> Birth Notebooks first.</span>}
          </p>
        </>
      )}
      <table>
        <thead><tr><th>Seq</th><th>Issued</th><th>Agents</th><th>Notebooks</th><th>Top-level</th><th>Hash</th></tr></thead>
        <tbody>
          {releases === null && <tr><td colSpan={6} className="hint">Loading…</td></tr>}
          {releases && releases.length === 0 && <tr><td colSpan={6} className="hint">No releases published for this project.</td></tr>}
          {releases?.map((r) => (
            <tr key={r.id}>
              <td className="mono">{r.seq}</td><td className="hint">{when(r.issued_at)}</td><td>{r.agent_count}</td><td>{r.notebook_count}</td>
              <td className="mono">{r.top_level_agent ?? '—'}</td><td className="mono hint" title={r.hash}>{short(r.hash)}…</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

/** One Notebook's imported rules: its tool allowlist and its forbids, each with where it came from. */
function NotebookRulesView({ rules, agent, settings }: { rules: Partial<NotebookRules>; agent: ProjectAgent | undefined; settings: ProjectSettings[] }) {
  const allow = rules.tool_allowlist
  const fb = rules.forbid_bash ?? []
  const ft = rules.forbid_tool ?? []
  return (
    <div className="hint">
      <div>
        Tools: {allow === undefined ? '—' : allow === null ? 'all tools (the agent file lists none)' : <span className="mono">{allow.join(', ')}</span>}
        {Array.isArray(allow) && agent && <> · from the import: <span className="mono">{agent.current.path_rel}</span></>}
      </div>
      {fb.length === 0 && ft.length === 0 && <div>Forbids: none</div>}
      {fb.map((r) => <div key={`b${r}`}>Forbids Bash <span className="mono">{r}</span>{forbidSource(settings, 'bash', r) && <> · from the import: {forbidSource(settings, 'bash', r)}</>}</div>)}
      {ft.map((r) => <div key={`t${r}`}>Forbids <span className="mono">{r}</span>{forbidSource(settings, 'tool', r) && <> · from the import: {forbidSource(settings, 'tool', r)}</>}</div>)}
    </div>
  )
}

function BirthAllPanel({ p, token, disabledReason, onDone, setError }: { p: ProjectDetail; token: string; disabledReason: string | null; onDone: (msg: string) => void; setError: (m: string | null) => void }) {
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<BirthAllResult | null>(null)
  const willBirth = p.agents.filter((a) => !a.notebook)
  const alreadyHave = p.agents.filter((a) => a.notebook)
  async function run() {
    setBusy(true); setError(null)
    const r = await api<BirthAllResult>(`/projects/${p.id}/notebooks/birth-all`, { method: 'POST', body: JSON.stringify({}) }, token)
    setBusy(false); setConfirming(false)
    if (!isOk(r)) { setError(r.error.code === 'assumed_no_grant' ? 'An assumed session cannot birth Notebooks.' : projectErrorText(r.error)); return }
    setResult(r.data)
    onDone(`Born ${r.data.born.length} Notebook${r.data.born.length === 1 ? '' : 's'}; skipped ${r.data.skipped.length}.`)
  }
  return (
    <div className="panel">
      {!confirming && (
        <p style={{ margin: 0 }}>
          <button className="btn" disabled={!!disabledReason || willBirth.length === 0} title={disabledReason ?? undefined} onClick={() => { setResult(null); setConfirming(true) }}>Birth and sign Notebooks for all agents</button>{' '}
          <span className="hint">{disabledReason ?? (p.agents.length > 0 && willBirth.length === 0 ? 'Every agent already has a Notebook.' : BIRTH_ALL_NOTE)}</span>
        </p>
      )}
      {confirming && (
        <>
          <h3 style={{ marginTop: 0 }}>Birth and sign Notebooks for all agents</h3>
          <p className="hint">{BIRTH_ALL_NOTE} They are signed with your organisation's key and ship in the next project release.</p>
          <h4>Will be born ({willBirth.length})</h4>
          <p className="mono">{willBirth.map((a) => a.name).join(', ')}</p>
          <h4>Skipped: already have a Notebook ({alreadyHave.length})</h4>
          <p className="mono">{alreadyHave.map((a) => a.name).join(', ') || '—'}</p>
          <p>
            <button className="btn" disabled={busy} onClick={run}>{busy ? 'Signing…' : `Birth ${willBirth.length} Notebook${willBirth.length === 1 ? '' : 's'}`}</button>{' '}
            <button className="btn ghost" disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
          </p>
        </>
      )}
      {result && (
        <div role="status">
          <h4>Born ({result.born.length})</h4>
          {result.born.length === 0 ? <p className="hint">None.</p> : (
            <ul style={{ margin: 0 }}>
              {result.born.map((b) => (
                <li key={b.notebook_id}>
                  <span className="mono">{b.agent}</span> — hash <span className="mono" title={b.compiled_hash}>{short(b.compiled_hash)}</span>
                  <NotebookRulesView rules={b} agent={p.agents.find((a) => a.name === b.agent)} settings={p.settings} />
                </li>
              ))}
            </ul>
          )}
          <h4>Skipped ({result.skipped.length})</h4>
          {result.skipped.length === 0 ? <p className="hint">None.</p> : <ul style={{ margin: 0 }}>{(result.skipped ?? []).map((x) => <li key={x.agent}><span className="mono">{x.agent}</span> — {x.reason === 'has_notebook' ? 'already has a Notebook' : x.reason === 'no_version' ? 'has no version to sign' : x.reason}</li>)}</ul>}
          <p className="hint" style={{ marginBottom: 0 }}>Next: set a <b>Top-level</b> agent on a row below, then <b>Publish release</b> under the agents.</p>
        </div>
      )}
    </div>
  )
}

function AgentsTab({ p, session, reload, setError, setNotice }: { p: ProjectDetail; session: Session; reload: () => void; setError: (m: string | null) => void; setNotice: (m: string | null) => void }) {
  const assumed = useSyncExternalStore(subscribeAssumed, getAssumed, getAssumed)
  const w = projectWriteState(session.user.role, assumed)
  const [open, setOpen] = useState<{ name: string; mode: 'edit' | 'notebook' | 'answers' | 'versions' } | null>(null)
  const [versionsKey, setVersionsKey] = useState(0)
  async function setTop(name: string | null) {
    setError(null); setNotice(null)
    const r = await api<unknown>(`/projects/${p.id}/top-level-agent`, { method: 'PUT', body: JSON.stringify({ name }) }, session.token)
    if (!isOk(r)) setError(projectErrorText(r.error)); else { setNotice(name ? `Top-level agent set to ${name}. It reaches Macs in the next release.` : 'Top-level agent cleared.'); reload() }
  }
  const done = (msg: string) => { setOpen(null); setError(null); setNotice(msg); reload() }
  return (
    <>
      {w.reason && <p className="hint">{w.reason}</p>}
      {w.visible && <BirthAllPanel p={p} token={session.token} disabledReason={w.reason} onDone={(m) => { setError(null); setNotice(m); reload() }} setError={setError} />}
      <table>
        <thead><tr><th>Agent</th><th>Model</th><th>Tools</th><th>Source</th><th>State</th><th></th></tr></thead>
        <tbody>
          {p.agents.length === 0 && <tr><td colSpan={6} className="hint">No agents in this project.</td></tr>}
          <ScopedRows items={p.agents} cols={6} row={(a) => {
            const s = agentSummary(a.current.frontmatter)
            return (
              <Fragment key={a.name}>
                <tr>
                  <td>
                    <span className="mono">{a.name}</span>{a.purpose && <div className="hint">{a.purpose}</div>}
                    <div className="hint mono">{a.current.path_rel}</div>
                    <PathsRemoved n={a.current.paths_removed} />
                    {a.shadowed.length > 0 && <div className="hint" title="Claude Code uses the nearest definition; these ship in no release">Shadows: {a.shadowed.map((x) => `${scopeLabel(x)} (${x.path_rel})`).join(', ')}</div>}
                  </td>
                  <td className="mono">{s.model}</td>
                  <td className="hint">{s.tools}</td>
                  <td><span className="pill">{a.current.source === 'tenant' ? 'refined here' : 'scanned'}</span></td>
                  <td>
                    {a.drift && <span className="pill drift" title="The folder holds something other than what this tenant manages">drift</span>}{' '}
                    {a.notebook
                      ? <span className="pill ok" title={`born from: ${notebookSourceLabel(a.notebook)}`}>Notebook</span>
                      : <span className="pill off">no Notebook</span>}{' '}
                    {a.top_level && <span className="pill owner">top-level</span>}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {a.notebook && <><button className="btn ghost sm" onClick={() => setOpen(open?.name === a.name && open.mode === 'versions' ? null : { name: a.name, mode: 'versions' })}>Versions</button>{' '}</>}
                    {w.visible && (
                      <>
                        <button className="btn sm" disabled={!!w.reason} onClick={() => setOpen({ name: a.name, mode: 'edit' })}>Edit</button>{' '}
                        {!a.notebook && <><button className="btn ghost sm" disabled={!!w.reason} onClick={() => setOpen({ name: a.name, mode: 'notebook' })}>Notebook</button>{' '}</>}
                        <button className="btn ghost sm" disabled={!!w.reason} onClick={() => setOpen({ name: a.name, mode: 'answers' })}>Answer questions</button>{' '}
                        {a.top_level
                          ? <button className="btn ghost sm" disabled={!!w.reason} onClick={() => setTop(null)}>Clear top-level</button>
                          : <button className="btn ghost sm" disabled={!!w.reason} onClick={() => setTop(a.name)}>Top-level</button>}
                      </>
                    )}
                  </td>
                </tr>
                {open?.name === a.name && (
                  <tr><td colSpan={6}>
                    {open.mode === 'edit' && <AgentEditor projectId={p.id} agent={a} token={session.token} onSaved={() => done(`Saved a new version of ${a.name}.`)} onClose={() => setOpen(null)} onError={setError} />}
                    {open.mode === 'notebook' && <NotebookPicker projectId={p.id} agent={a} token={session.token} role={session.user.role as TenantRole} onDone={() => done(`Born a Notebook for ${a.name}.`)} onClose={() => setOpen(null)} onError={setError} />}
                    {open.mode === 'answers' && <AnswersPanel projectId={p.id} agent={a} p={p} token={session.token} role={session.user.role as TenantRole} onSigned={(r) => { setError(null); setNotice(`Signed version ${r.notebook_version} of ${a.name}. It reaches the Mac in the next project release.`); setVersionsKey((k) => k + 1); reload() }} onClose={() => setOpen(null)} />}
                    {open.mode === 'versions' && <NotebookVersionsList projectId={p.id} agent={a.name} token={session.token} refreshKey={versionsKey} />}
                  </td></tr>
                )}
              </Fragment>
            )
          }} />
        </tbody>
      </table>
    </>
  )
}

function HistoryTab({ id, token }: { id: string; token: string }) {
  const [items, setItems] = useState<SnapshotMeta[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    api<ProjectPage<SnapshotMeta>>(`/projects/${id}/snapshots`, {}, token).then((r) => {
      if (!alive) return
      if (isOk(r)) setItems(r.data.items); else { setItems([]); setErr(r.error.message) }
    })
    return () => { alive = false }
  }, [id, token])
  return (
    <>
      {err && <p className="err" role="alert">{err}</p>}
      <table>
        <thead><tr><th>Scanned</th><th>Source</th><th>Agents</th><th>Changed</th><th>Size</th><th>Hash</th></tr></thead>
        <tbody>
          {items === null && <tr><td colSpan={6} className="hint">Loading…</td></tr>}
          {items && items.length === 0 && <tr><td colSpan={6} className="hint">No snapshots.</td></tr>}
          {items?.map((s) => (
            <tr key={s.id}>
              <td className="hint">{when(s.scanned_at)}<div>{s.client_build}</div></td>
              <td>{s.source === 'import' ? 'imported file' : 'Mac'}</td><td>{s.agent_count}</td><td>{s.agents_changed_count}</td>
              <td className="hint">{(s.size / 1024).toFixed(1)} KiB</td><td className="mono hint" title={s.sha256}>{short(s.sha256)}…</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

/** The project's team and, for each person working here, their effective Notebooks (§6v): each agent plus top-level sessions. */
function PeopleTab({ projectId, session, setNotice }: { projectId: string; session: Session; setNotice: (m: string | null) => void }) {
  const token = session.token
  const assumed = useSyncExternalStore(subscribeAssumed, getAssumed, getAssumed)
  const [eff, setEff] = useState<Effective | null>(null)
  const [team, setTeam] = useState<{ id: string; name: string } | null | 'unknown'>('unknown')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<Rebirth | null>(null)
  const load = useCallback(async () => {
    const [e, t] = await Promise.all([api<Effective>(effectivePath(projectId), {}, token), api<LevelTree>('/levels/tree', {}, token)])
    if (isOk(e)) { setEff(e.data); setErr(null) } else { setEff({ people: [] }); setErr(levelErrorText(e.error)) }
    setTeam(isOk(t) ? t.data.teams.find((x) => x.projects.some((p) => p.id === projectId)) ?? null : 'unknown')
  }, [projectId, token])
  useEffect(() => { load() }, [load])
  const staleCount = eff ? (eff.people ?? []).reduce((n, p) => n + (p.entries ?? []).filter((x) => x.stale).length, 0) : 0
  const mayRebirth = canEditHierarchy(session.user.role)
  const blocked = assumed?.mode === 'read' ? 'This is an assumed read-only session.' : null
  async function rebirth() {
    setBusy(true); setErr(null); setResult(null)
    const r = await api<Rebirth>(rebirthAffectedPath(projectId), { method: 'POST', body: '{}' }, token)
    setBusy(false)
    if (!isOk(r)) { setErr(levelErrorText(r.error)); return }
    setResult(r.data); setNotice(`Re-signed ${r.data.count} effective Notebook${r.data.count === 1 ? '' : 's'}.`); load()
  }
  return (
    <div className="effective">
      <p>
        Team: {team === 'unknown' ? <span className="hint">…</span> : team ? <a href={href(`/hierarchy/teams/${team.id}`)}>{team.name}</a> : <span className="hint">none: this project inherits straight from Corporate</span>}
        {' · '}<a href={href(`/hierarchy/projects/${projectId}`)}>Project Notebook</a> · <a href={href('/hierarchy')}>Hierarchy</a>
      </p>
      {err && <p className="err" role="alert">{err}</p>}
      {mayRebirth && (
        <p>
          <button className="btn" disabled={busy || staleCount === 0 || !!blocked} title={blocked ?? undefined} onClick={rebirth}>{busy ? 'Signing…' : 'Re-birth all affected'}</button>{' '}
          <span className="hint">{blocked ?? (staleCount === 0 ? 'Nothing is stale.' : `${staleCount} stale. Each is rebuilt and signed by you.`)}</span>
        </p>
      )}
      {result && (result.skipped ?? []).length > 0 && (
        <p className="hint" role="status">Skipped: {(result.skipped ?? []).map((x) => `${x.agent === 'user_session' ? 'top-level sessions' : x.agent} (${x.reason === 'no_user_notebook' ? 'no User Notebook yet' : x.reason})`).join(', ')}</p>
      )}
      {eff === null && <p className="hint">Loading…</p>}
      {eff && (eff.people ?? []).length === 0 && <p className="hint">No one is working in this project yet. A person appears once a Mac of theirs is linked to it.</p>}
      {eff?.people?.map((person) => (
        <div className="panel" key={person.user_id}>
          <h3 style={{ marginTop: 0 }}><a href={href(`/hierarchy/users/${person.user_id}`)}>{person.name || person.email}</a> <span className="hint">{person.email}</span></h3>
          {(person.entries ?? []).length === 0 && <p className="hint">{person.has_user_notebook === false ? 'No effective Notebooks: this person has no User Notebook yet.' : 'No effective Notebooks yet.'}</p>}
          {(person.entries ?? []).length > 0 && (
            <table>
              <thead><tr><th>Set</th><th>Parents (level · version)</th><th>Built</th><th>State</th></tr></thead>
              <tbody>
                {(person.entries ?? []).map((en) => (
                  <tr key={en.id}>
                    <td>{en.role === 'user_session' ? 'Top-level sessions' : <span className="mono">{en.agent}</span>}<div className="hint mono" title={en.compiled_hash}>{short(en.compiled_hash)}…</div></td>
                    <td><div className="parents">{(en.parents ?? []).map((x) => <span key={x.notebook_id + x.level} className="pill" title={x.notebook_id}>{levelLabel(x.level)} v{x.version}</span>)}</div></td>
                    <td className="hint">{when(en.built_at)}</td>
                    <td>{en.stale ? <span className="pill stale" title="A parent changed since this was signed">stale</span> : <span className="pill ok">current</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  )
}

function Body({ text }: { text: string }) {
  return <details><summary className="hint">Show</summary><pre className="body">{text}</pre></details>
}

export default function Project({ session, projectId }: { session: Session; projectId: string }) {
  const [p, setP] = useState<ProjectDetail | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('Agents')

  const load = useCallback(async () => {
    const r = await api<ProjectDetail>(`/projects/${projectId}`, {}, session.token)
    if (isOk(r)) { setP(r.data); setLoadErr(null) } else setLoadErr(r.error.code === 'not_found' ? 'This project does not exist.' : r.error.message)
  }, [projectId, session.token])
  useEffect(() => { setP(null); load() }, [load])

  if (loadErr) return <div className="projects"><p className="err" role="alert">{loadErr}</p><p><a href={href('/projects')}>‹ Projects</a></p></div>
  if (!p) return <div className="projects"><p className="hint">Loading…</p></div>

  return (
    <div className="projects">
      <p className="hint"><a href={href('/projects')}>‹ Projects</a></p>
      <h2>{p.name}</h2>
      {p.description && <p className="hint">{p.description}</p>}
      <p className="hint">Last scan {when(p.latest_snapshot?.scanned_at)}{p.latest_snapshot ? ` · ${p.latest_snapshot.client_build}` : ''} · {p.linked_machines.length} linked Mac{p.linked_machines.length === 1 ? '' : 's'}</p>
      {error && <p className="err" role="alert">{error}</p>}
      {notice && <p className="hint" role="status">{notice}</p>}
      <div className="tabs" role="tablist">
        {TABS.map((t) => <button key={t} role="tab" aria-selected={tab === t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>{t}</button>)}
      </div>
      {tab === 'Agents' && (
        <>
          <AgentsTab p={p} session={session} reload={load} setError={setError} setNotice={setNotice} />
          <ReleasesPanel p={p} session={session} onChanged={load} />
        </>
      )}
      {tab === 'Repos' && (
        <table>
          <thead><tr><th>Repo</th><th>Remote</th><th>Branch</th><th>Worktrees</th></tr></thead>
          <tbody>
            {p.repos.length === 0 && <tr><td colSpan={4} className="hint">No repos in the latest scan.</td></tr>}
            {p.repos.map((r) => (
              <tr key={r.root_rel}>
                <td className="mono">{r.root_rel}</td><td className="mono hint">{r.remote}</td><td className="mono">{r.branch}</td>
                <td>{r.worktrees.length === 0 ? <span className="hint">none</span> : r.worktrees.map((w) => <div key={w.name}><span className="mono">{w.name}</span> <span className="hint">{w.branch} · {w.location}</span></div>)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {tab === 'Rules' && (
        <table>
          <thead><tr><th>File</th><th>Kind</th><th>Content</th></tr></thead>
          <tbody>
            {p.rules.length === 0 && <tr><td colSpan={3} className="hint">No rules in the latest scan.</td></tr>}
            <ScopedRows items={p.rules} cols={3} row={(r) => <tr key={`${r.scope}${r.depth}${r.path_rel}`}><td className="mono">{r.path_rel}<PathsRemoved n={r.paths_removed} /></td><td className="hint">{r.kind === 'claude_md' ? 'CLAUDE.md' : 'rule'}</td><td><Body text={r.body} /></td></tr>} />
          </tbody>
        </table>
      )}
      {tab === 'Skills' && (
        <table>
          <thead><tr><th>Skill</th><th>File</th><th>Content</th></tr></thead>
          <tbody>
            {p.skills.length === 0 && <tr><td colSpan={3} className="hint">No skills in the latest scan.</td></tr>}
            <ScopedRows items={p.skills} cols={3} row={(s) => <tr key={`${s.scope}${s.depth}${s.path_rel}`}><td className="mono">{s.name}</td><td className="mono hint">{s.path_rel}<PathsRemoved n={s.paths_removed} /></td><td><Body text={s.body} /></td></tr>} />
          </tbody>
        </table>
      )}
      {tab === 'Settings' && (
        <table>
          <thead><tr><th>File</th><th>Hook events</th><th>Permissions</th><th>MCP servers</th></tr></thead>
          <tbody>
            {p.settings.length === 0 && <tr><td colSpan={4} className="hint">No settings files in the latest scan.</td></tr>}
            <ScopedRows items={p.settings} cols={4} row={(s) => (
              <tr key={`${s.scope}${s.depth}${s.path_rel}`}>
                <td className="mono">{s.path_rel}</td>
                <td className="mono">{s.hook_events.join(', ') || '—'}</td>
                <td>
                  {s.permissions.allow} allow · {s.permissions.deny} deny
                  <PatternList label="Deny" note="enforced" items={s.permission_patterns?.deny} />
                  <PatternList label="Allow" note="not enforced" items={s.permission_patterns?.allow} />
                  <PatternList label="Ask" note="not enforced" items={s.permission_patterns?.ask} />
                  <PathsRemoved n={s.paths_removed} />
                </td>
                <td className="mono">{s.mcp_servers.join(', ') || '—'}</td>
              </tr>
            )} />
          </tbody>
        </table>
      )}
      {tab === 'People' && <PeopleTab projectId={p.id} session={session} setNotice={setNotice} />}
      {tab === 'History' && <HistoryTab id={p.id} token={session.token} />}
    </div>
  )
}
